import { BORNES_PAPIER } from '@nextilt/commun';
import type { EnvoiDUnPapier, Papier } from '../partage/api/types';

/**
 * Les règles pures de la liste libre posée sur la carte (chantier post-it-et-listes, ticket 04),
 * sans React ni réseau, testées dans `tests/client/papiers/liste.test.ts`.
 *
 * Décision d'Igor du 2026-10-19 : « c'est une todo qui na pas de lien avec des bulle ». Ses lignes
 * sont du texte à cocher, pas des bulles : rien ici ne touche au moteur, cocher une ligne ne
 * change ni une bulle, ni Maintenant. Une ligne cochée est barrée et reste en place.
 */

type Ligne = Papier['lignes'][number];

/**
 * Ce qu'on envoie pour poser une liste : une feuille blanche à lignes, la plus proche de la
 * maquette (`.liste`). Ni place ni taille : le serveur propose une place libre et la taille d'une
 * liste neuve.
 */
export function listeNeuve(): EnvoiDUnPapier {
  return { sorte: 'liste', fond: 'lignes' };
}

/** Une ligne de plus est possible, sous le nombre de lignes de `commun/`. */
export function peutAjouterUneLigne(lignes: readonly Ligne[]): boolean {
  return lignes.length < BORNES_PAPIER.lignesMax;
}

/**
 * On quitte une ligne : vidée (ou jamais écrite), elle est retirée ; changée, son texte part sans
 * espaces autour ; sinon rien.
 */
export function quitterUneLigne(
  ligne: Pick<Ligne, 'texte'>,
  ecrit: string
): { sorte: 'retirer' } | { sorte: 'ecrire'; texte: string } | null {
  const propre = ecrit.trim();
  if (propre === '') return { sorte: 'retirer' };
  return propre === ligne.texte ? null : { sorte: 'ecrire', texte: propre };
}

/** Ce que fait Entrée, ou un effacement dans une ligne vide : où va le curseur. */
export type OuVaLeCurseur = { sorte: 'ajouter' } | { sorte: 'aller'; id: string } | { sorte: 'quitter' } | { sorte: 'titre' };

/**
 * Entrée dans une ligne. Dans la dernière, écrite : une ligne suivante est créée. Dans une ligne
 * du milieu : le curseur passe à la suivante (une ligne neuve va toujours à la fin, l'ordre ne
 * change pas). Dans une ligne vide, ou au nombre maximal de lignes : on la quitte.
 */
export function apresEntree(lignes: readonly Ligne[], id: string, ecrit: string): OuVaLeCurseur {
  if (ecrit.trim() === '') return { sorte: 'quitter' };
  const i = lignes.findIndex((l) => l.id === id);
  const suivante = lignes[i + 1];
  if (i !== -1 && suivante) return { sorte: 'aller', id: suivante.id };
  return peutAjouterUneLigne(lignes) ? { sorte: 'ajouter' } : { sorte: 'quitter' };
}

/** Entrée dans le titre : la première ligne, créée si la liste est vide. */
export function apresEntreeDuTitre(lignes: readonly Ligne[]): OuVaLeCurseur {
  const premiere = lignes[0];
  return premiere ? { sorte: 'aller', id: premiere.id } : { sorte: 'ajouter' };
}

/** Effacer dans une ligne vide : le curseur remonte à la ligne d'avant, ou au titre. */
export function apresEffacement(lignes: readonly Ligne[], id: string): OuVaLeCurseur {
  const i = lignes.findIndex((l) => l.id === id);
  const avant = i > 0 ? lignes[i - 1] : undefined;
  return avant ? { sorte: 'aller', id: avant.id } : { sorte: 'titre' };
}

/** Le papier avec une ligne écrite, cochée ou décochée, à sa place : ce que l'écran montre tout de suite. */
export function avecLaLigne(papier: Papier, ligneId: string, changement: Partial<Pick<Ligne, 'texte' | 'cochee'>>): Papier {
  return { ...papier, lignes: papier.lignes.map((l) => (l.id === ligneId ? { ...l, ...changement } : l)) };
}

/** Le papier sans cette ligne, les autres dans le même ordre. */
export function sansLaLigne(papier: Papier, ligneId: string): Papier {
  return { ...papier, lignes: papier.lignes.filter((l) => l.id !== ligneId) };
}

/** La ligne que le serveur rend en plus après un ajout, où le curseur se met. */
export function laLigneNeuve(avant: Papier, apres: Papier): string | null {
  const connues = new Set(avant.lignes.map((l) => l.id));
  return apres.lignes.find((l) => !connues.has(l.id))?.id ?? null;
}
